import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Modal from "@/components/common/Modal";
import { useTeams } from "../../staff/hooks/useTeams";
import { useStaff } from "@/components/pages/staff/hooks/useStaff";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { Button } from "@/components/ui/button";

interface Props {
  assignedStaffId?: string;
  setAssignedStaffId?: (staffId?: string) => void;
  teamId?: string;
  setTeamId?: (teamId?: string) => void;
}

const AssignStaffOrTeamCell: React.FC<Props> = ({
  assignedStaffId,
  setAssignedStaffId,
  teamId,
  setTeamId,
}) => {
  const [open, setOpen] = React.useState(false);
  const { userProfile } = useAuthenticatedUser();
  const selectedHotelId = useSelectedHotelId();
  const hotelId = selectedHotelId ?? userProfile?.profile?.hotelId;

  const { data: staffData, isLoading: isStaffLoading } = useStaff({
    hotelId: hotelId as string,
  });
  const { data: teamsData, isLoading: isTeamsLoading } = useTeams({
    isActive: true,
    perPage: 100,
    sortBy: null,
    sortOrder: null,
  });

  const handleStaffChange = ({
    newStaffId,
    teamId,
  }: {
    newStaffId?: string;
    teamId?: string;
  }) => {
    setAssignedStaffId?.(newStaffId);
    setTeamId?.(teamId);

    setOpen(false);
  };

  const staffMembers = React.useMemo(
    () =>
      staffData?.users.flatMap((user) => {
        if (!user.staff) {
          return [];
        }

        return [
          {
            staffId: user.staff.id,
            staffName:
              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
              user.email,
          },
        ];
      }) || [],
    [staffData?.users],
  );

  const triggerLabel = React.useMemo(() => {
    if (assignedStaffId) {
      return staffMembers.find((s) => s.staffId === assignedStaffId)?.staffName;
    }
    if (teamId) {
      return teamsData?.data?.find((t) => t.id === teamId)?.name;
    }
    return "Assign Staff or Team";
  }, [assignedStaffId, staffMembers, teamId, teamsData]);

  return (
    <>
      <Modal
        title="Assign Staff or Team"
        trigger={
          <Button
            type="button"
            variant="new_outline"
            className="h-12 w-full justify-start"
            onClick={() => setOpen(true)}
          >
            {triggerLabel}
          </Button>
        }
        open={open}
        onOpenChange={setOpen}
        className="flex flex-col items-center justify-center py-6"
      >
        <span className="text-md font-medium text-gray-700">Staff</span>
        <div className="w-full max-w-sm">
          <Select
            defaultValue={assignedStaffId}
            onValueChange={(s) =>
              handleStaffChange({
                newStaffId: s,
                teamId: undefined,
              })
            }
            disabled={isStaffLoading}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  isStaffLoading ? "Loading..." : "Select staff member"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {staffMembers.map((staff) => (
                <SelectItem key={staff.staffId} value={staff.staffId}>
                  {staff.staffName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <span className="text-md my-2 font-medium text-gray-700">or</span>

        <span className="text-md font-medium text-gray-700">Team</span>
        <div className="w-full max-w-sm">
          <Select
            defaultValue={teamId}
            onValueChange={(t) =>
              handleStaffChange({
                teamId: t,
                newStaffId: undefined,
              })
            }
            disabled={isTeamsLoading}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={isTeamsLoading ? "Loading..." : "Select team"}
              />
            </SelectTrigger>
            <SelectContent>
              {teamsData?.data?.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Modal>
    </>
  );
};

export default AssignStaffOrTeamCell;
